'use client';

import type { ImageType } from '@eliadmin/core/src/api/api-types';

import { CarouselControls } from '@eliadmin/core/src/components/ui/carousel-controls';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { GoArrowLeft, GoArrowRight } from 'react-icons/go';
import { CarouselWall } from '../ui/carousel-wall';
import { Carousel } from '@eliadmin/core/src/components/ui/carousel';
import { useCarouselContext } from '@eliadmin/core/src/components/ui/carousel-context';
import { CarouselProgress } from '../ui/carousel-progress';
import { MemberCard } from './member-card';

export interface Member {
  name: string;
  image: ImageType;
  role: string;
  surname: string;
}

export function Team({ members, title }: { members: Member[]; title: string }) {
  const options = {
    align: 'start' as const,
  };

  const carouselContext = useCarouselContext();

  if (!carouselContext) {
    throw new Error('Hero must be used within a CarouselProvider');
  }

  const { selectedIndex } = carouselContext;

  return (
    <section
      id="nas-tim"
      className={cn(
        'custom-section',
        'gap-8 pb-8 pt-12 sm:gap-10 sm:pb-10 sm:pt-16 lg:gap-12 lg:pb-16 lg:pt-[120px]',
      )}
    >
      <div className={cn('custom-container', 'flex items-center justify-between gap-4')}>
        <h2 className="font-oswald text-3xl font-medium uppercase text-black sm:text-4xl lg:text-5xl">
          {title}
        </h2>
        <CarouselControls
          className={{
            main: 'hidden gap-2 sm:flex',
            button: 'h-12 w-12 rounded-none border-none bg-primary shadow-none hover:bg-primary/90',
          }}
          nextIcon={<GoArrowRight size={24} className="text-white" />}
          prevIcon={<GoArrowLeft size={24} className="text-white" />}
        />
      </div>
      <div className={cn('custom-container', 'relative overflow-visible')}>
        <CarouselWall className="bg-white" />
        <Carousel
          options={options}
          className={{
            slide: 'w-full max-w-[282px]',
            wrapper: 'w-full overflow-visible',
            container: 'w-full gap-4 overflow-visible sm:gap-5 lg:gap-6',
            main: 'w-full overflow-visible',
          }}
          slides={members.map(member => (
            <MemberCard member={member} key={member?.name} />
          ))}
        />
      </div>
      <div
        className={cn('custom-container', 'flex items-center justify-between gap-4 pr-4 sm:hidden')}
      >
        <CarouselProgress selectedIndex={selectedIndex} totalSlides={members?.length} />
        <CarouselControls
          className={{
            main: 'flex gap-2',
            button:
              'max-h-10 min-h-10 min-w-10 max-w-10 rounded-none border-none bg-primary shadow-none hover:bg-primary/90',
          }}
          nextIcon={<GoArrowRight size={20} className="text-white" />}
          prevIcon={<GoArrowLeft size={20} className="text-white" />}
        />
      </div>
    </section>
  );
}
